import { queryClass, updateObject, createObject } from '@/services/adapters/parse/parse-client'
import type { Promotion, PromotionQueryParams, PromotionStatus } from './promotion.types'

export class PromotionService {
  /**
   * Map raw Parse data to Promotion interface
   */
  private static mapToPromotion(raw: any): Promotion {
    // Helper to safely get boolean values
    const getBoolean = (value: any): boolean => {
      return value === true || value === 'true' || value === 1 || value === '1'
    }

    // Helper to safely get date
    const getDate = (value: any): Date | undefined => {
      if (!value) return undefined
      if (value instanceof Date) return value
      if (typeof value === 'string') return new Date(value)
      if (value.iso) return new Date(value.iso) // Parse Date format
      return undefined
    }

    return {
      id: raw.objectId,
      objectId: raw.objectId,
      name: raw.name || '',
      code: raw.code || '',
      description: raw.description,
      promotion_type: raw.promotion_type || 1,
      promotion_method: raw.promotion_method || 'all_member',
      category: raw.category || 'other',
      deposit_condition_type: raw.deposit_condition_type,
      minigame_condition_type: raw.minigame_condition_type,
      type: raw.type,
      amount: raw.amount,
      deposit_min: raw.deposit_min,
      withdraw_limit: raw.withdraw_limit,
      deposit_turnover_bonus_x: raw.deposit_turnover_bonus_x,
      accepted_max_perday: raw.accepted_max_perday,
      startDate: getDate(raw.startDate),
      endDate: getDate(raw.endDate),
      status: raw.status || 'Inactive',
      autoSent: getBoolean(raw.autoSent),
      returnCredit: getBoolean(raw.returnCredit),
      userOnlyFirstDeposit: getBoolean(raw.userOnlyFirstDeposit),
      rewards: raw.rewards || [],
      bonusConditions: raw.bonusConditions || [],
      playConditions: raw.playConditions || [],
      createdAt: raw.createdAt ? new Date(raw.createdAt).toISOString() : new Date().toISOString(),
      updatedAt: raw.updatedAt ? new Date(raw.updatedAt).toISOString() : new Date().toISOString(),
      deletedAt: raw.deletedAt ? new Date(raw.deletedAt).toISOString() : undefined,
      
      // Phase 1: New Fields
      max_usage_total: raw.max_usage_total ?? 0,
      max_usage_per_member: raw.max_usage_per_member ?? 0,
      budget_limit: raw.budget_limit ?? 0,
      priority: raw.priority ?? 0,
      is_exclusive: getBoolean(raw.is_exclusive),
      member_groups: raw.member_groups || [],
      image_url: raw.image_url || '',
      usage_count: raw.usage_count ?? 0,
      total_spent: raw.total_spent ?? 0,
    }
  }

  /**
   * Find promotions with pagination and filters
   */
  static async find(
    sessionToken: string,
    params: PromotionQueryParams = {}
  ): Promise<{ items: Promotion[]; total: number }> {
    const where: Record<string, any> = {
      deletedAt: { $exists: false },
    }

    // Search by code
    if (params.query) {
      where.code = { $regex: params.query.toUpperCase(), $options: 'i' }
    }

    const queryOptions: any = {
      where,
      limit: params.limit || 10,
      skip: params.page ? (params.page - 1) * (params.limit || 10) : 0,
      order: '-createdAt', // Latest first
    }

    // Add sorting if provided
    if (params.sort?.field) {
      queryOptions.order = params.sort.direction === 'asc' 
        ? params.sort.field 
        : `-${params.sort.field}`
    }

    const result = await queryClass('Promotion', queryOptions, sessionToken)

    return {
      items: (result.results || []).map(this.mapToPromotion),
      total: result.count || 0,
    }
  }

  /**
   * Find one promotion by ID
   */
  static async findOne(sessionToken: string, id: string): Promise<Promotion | null> {
    const result = await queryClass(
      'Promotion',
      {
        where: {
          objectId: id,
          deletedAt: { $exists: false },
        },
        limit: 1,
      },
      sessionToken
    )

    if (result.results && result.results.length > 0) {
      return this.mapToPromotion(result.results[0])
    }

    return null
  }

  /**
   * Check if promotion code exists
   */
  static async checkCodeExists(
    sessionToken: string,
    code: string,
    excludeId?: string
  ): Promise<boolean> {
    const where: Record<string, any> = {
      code: code.toUpperCase(),
      deletedAt: { $exists: false },
    }

    if (excludeId) {
      where.objectId = { $ne: excludeId }
    }

    const result = await queryClass(
      'Promotion',
      {
        where,
        limit: 1,
      },
      sessionToken
    )

    return (result.results?.length || 0) > 0
  }

  /**
   * Create a new promotion
   */
  static async create(
    _sessionToken: string,
    data: Partial<Promotion>
  ): Promise<Promotion> {
    console.log('🎁 [PromotionService] Creating promotion:', data)
    
    // Prepare data for Parse
    const createData: Record<string, any> = {
      name: data.name,
      code: data.code?.toUpperCase(),
      description: data.description || '',
      promotion_type: data.promotion_type || 1,
      promotion_method: data.promotion_method || 'all_member',
      category: data.category || 'other',
      status: data.status || 'Inactive',
      autoSent: data.autoSent || false,
      returnCredit: data.returnCredit || false,
      userOnlyFirstDeposit: data.userOnlyFirstDeposit || false,
    }

    // Add optional fields if they exist
    if (data.deposit_condition_type !== undefined) {
      createData.deposit_condition_type = data.deposit_condition_type
    }
    if (data.minigame_condition_type !== undefined) {
      createData.minigame_condition_type = data.minigame_condition_type
    }
    if (data.type !== undefined) {
      createData.type = data.type
    }
    if (data.amount !== undefined) {
      createData.amount = Number(data.amount)
    }
    if (data.deposit_min !== undefined) {
      createData.deposit_min = Number(data.deposit_min)
    }
    if (data.withdraw_limit !== undefined) {
      createData.withdraw_limit = Number(data.withdraw_limit)
    }
    if (data.deposit_turnover_bonus_x !== undefined) {
      createData.deposit_turnover_bonus_x = Number(data.deposit_turnover_bonus_x)
    }
    if (data.accepted_max_perday !== undefined) {
      createData.accepted_max_perday = Number(data.accepted_max_perday)
    }

    // Handle dates
    if (data.startDate) {
      createData.startDate = { __type: 'Date', iso: new Date(data.startDate).toISOString() }
    }
    if (data.endDate) {
      createData.endDate = { __type: 'Date', iso: new Date(data.endDate).toISOString() }
    }

    // Handle arrays
    if (data.rewards && data.rewards.length > 0) {
      createData.rewards = data.rewards
    }
    if (data.bonusConditions && data.bonusConditions.length > 0) {
      createData.bonusConditions = data.bonusConditions
    }
    if (data.playConditions && data.playConditions.length > 0) {
      createData.playConditions = data.playConditions
    }

    // Phase 1: New Fields
    if (data.max_usage_total !== undefined) {
      createData.max_usage_total = Number(data.max_usage_total)
    }
    if (data.max_usage_per_member !== undefined) {
      createData.max_usage_per_member = Number(data.max_usage_per_member)
    }
    if (data.budget_limit !== undefined) {
      createData.budget_limit = Number(data.budget_limit)
    }
    if (data.priority !== undefined) {
      createData.priority = Number(data.priority)
    }
    if (data.is_exclusive !== undefined) {
      createData.is_exclusive = data.is_exclusive
    }
    if (data.member_groups && data.member_groups.length > 0) {
      createData.member_groups = data.member_groups
    }
    if (data.image_url) {
      createData.image_url = data.image_url
    }
    // Initialize analytics fields
    createData.usage_count = 0
    createData.total_spent = 0

    const result = await createObject('Promotion', createData)
    console.log('✅ [PromotionService] Created promotion:', result)
    
    return this.mapToPromotion(result)
  }

  /**
   * Update a promotion
   */
  static async update(
    sessionToken: string,
    id: string,
    data: Partial<Promotion>
  ): Promise<Promotion> {
    console.log('🎁 [PromotionService] Updating promotion:', id, data)
    
    // Prepare update data
    const updateData: Record<string, any> = {}

    // Add fields that are present
    if (data.name !== undefined) updateData.name = data.name
    if (data.code !== undefined) updateData.code = data.code.toUpperCase()
    if (data.description !== undefined) updateData.description = data.description
    if (data.promotion_type !== undefined) updateData.promotion_type = data.promotion_type
    if (data.promotion_method !== undefined) updateData.promotion_method = data.promotion_method
    if (data.category !== undefined) updateData.category = data.category
    if (data.deposit_condition_type !== undefined) updateData.deposit_condition_type = data.deposit_condition_type
    if (data.minigame_condition_type !== undefined) updateData.minigame_condition_type = data.minigame_condition_type
    if (data.type !== undefined) updateData.type = data.type
    if (data.amount !== undefined) updateData.amount = Number(data.amount)
    if (data.deposit_min !== undefined) updateData.deposit_min = Number(data.deposit_min)
    if (data.withdraw_limit !== undefined) updateData.withdraw_limit = Number(data.withdraw_limit)
    if (data.deposit_turnover_bonus_x !== undefined) updateData.deposit_turnover_bonus_x = Number(data.deposit_turnover_bonus_x)
    if (data.accepted_max_perday !== undefined) updateData.accepted_max_perday = Number(data.accepted_max_perday)
    if (data.status !== undefined) updateData.status = data.status
    if (data.autoSent !== undefined) updateData.autoSent = data.autoSent
    if (data.returnCredit !== undefined) updateData.returnCredit = data.returnCredit
    if (data.userOnlyFirstDeposit !== undefined) updateData.userOnlyFirstDeposit = data.userOnlyFirstDeposit

    // Handle dates
    if (data.startDate) {
      updateData.startDate = { __type: 'Date', iso: new Date(data.startDate).toISOString() }
    }
    if (data.endDate) {
      updateData.endDate = { __type: 'Date', iso: new Date(data.endDate).toISOString() }
    }

    // Handle arrays
    if (data.rewards !== undefined) updateData.rewards = data.rewards
    if (data.bonusConditions !== undefined) updateData.bonusConditions = data.bonusConditions
    if (data.playConditions !== undefined) updateData.playConditions = data.playConditions

    // Phase 1: New Fields
    if (data.max_usage_total !== undefined) updateData.max_usage_total = Number(data.max_usage_total)
    if (data.max_usage_per_member !== undefined) updateData.max_usage_per_member = Number(data.max_usage_per_member)
    if (data.budget_limit !== undefined) updateData.budget_limit = Number(data.budget_limit)
    if (data.priority !== undefined) updateData.priority = Number(data.priority)
    if (data.is_exclusive !== undefined) updateData.is_exclusive = data.is_exclusive
    if (data.member_groups !== undefined) updateData.member_groups = data.member_groups
    if (data.image_url !== undefined) updateData.image_url = data.image_url

    await updateObject('Promotion', id, updateData)
    console.log('✅ [PromotionService] Updated promotion:', id)
    
    // Fetch the updated promotion
    const updated = await this.findOne(sessionToken, id)
    if (!updated) {
      throw new Error('Failed to fetch updated promotion')
    }
    return updated
  }

  /**
   * Delete a promotion (soft delete)
   */
  static async delete(_sessionToken: string, id: string): Promise<void> {
    console.log('🗑️ [PromotionService] Deleting promotion:', id)
    
    // Soft delete: set deletedAt and status to Inactive
    await updateObject('Promotion', id, {
      deletedAt: { __type: 'Date', iso: new Date().toISOString() },
      status: 'Inactive'
    })
    
    console.log('✅ [PromotionService] Deleted promotion:', id)
  }

  /**
   * Update promotion status (Active/Inactive)
   */
  static async updateStatus(
    _sessionToken: string,
    id: string,
    status: PromotionStatus
  ): Promise<void> {
    await updateObject('Promotion', id, { status })
  }
}

